import { apiClient } from '../../apiHelper';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import i18n from '@ohif/i18n';

import {
  formatCollaborationLocationLabel,
} from '../../../UI/Components/Collaboration/collaboration-labels';
import { storeExamViewerReturn } from '../../examViewerUrl';

const NOTIFICATIONS_NS = 'Notifications';

export interface AppNotification {
  id: string;
  type: string;
  content: string;
  data?: Record<string, unknown> | null;
  priority?: string;
  category?: string | null;
  is_read: boolean;
  created_at: string;
}

export interface UnifiedNotificationPayload {
  id?: string;
  type?: string;
  category?: string;
  content?: string;
  data?: Record<string, unknown> | null;
  priority?: string;
  created_at?: string;
  is_read?: boolean;
}

export const NOTIFICATION_RECENT_HOURS = 24;
export const NOTIFICATION_PAGE_SIZE = 50;

export type NotificationListParams = {
  scope?: 'recent' | 'all';
  hours?: number;
  limit?: number;
  offset?: number;
};

export const NOTIFICATION_RECENT_LIST_PARAMS: NotificationListParams = {
  scope: 'recent',
  hours: NOTIFICATION_RECENT_HOURS,
  limit: NOTIFICATION_PAGE_SIZE,
};

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params?: NotificationListParams) =>
    [
      ...notificationKeys.all,
      'list',
      params ?? NOTIFICATION_RECENT_LIST_PARAMS,
    ] as const,
  unread: (hours: number = NOTIFICATION_RECENT_HOURS) =>
    [...notificationKeys.all, 'unread-count', hours] as const,
};

export interface NotificationListResult {
  items: AppNotification[];
  total_count: number;
  total_all: number;
  unread_count: number;
  has_more: boolean;
}

/** @deprecated Prefer server-side `hours` filter via notificationService.list(). */
export function filterRecentNotifications(
  items: AppNotification[],
  hours: number = NOTIFICATION_RECENT_HOURS
): AppNotification[] {
  const cutoff = dayjs().subtract(hours, 'hour');
  return items.filter((item) => dayjs(item.created_at).isAfter(cutoff));
}

export function countUnread(items: AppNotification[]): number {
  return items.filter((n) => !n.is_read).length;
}

function unwrapList<T>(response: unknown): T[] {
  const body = response as { data?: T[] | { data?: T[] } };
  const nested = body?.data;
  if (Array.isArray(nested)) return nested;
  if (nested && Array.isArray((nested as { data?: T[] }).data)) {
    return (nested as { data: T[] }).data;
  }
  return Array.isArray(response) ? (response as T[]) : [];
}

function unwrapUnread(response: unknown): number {
  const body = response as { data?: { unread?: number } | number };
  if (typeof body?.data === 'number') return body.data;
  if (body?.data && typeof (body.data as { unread?: number }).unread === 'number') {
    return (body.data as { unread: number }).unread;
  }
  return 0;
}

function unwrapListResult(response: unknown): NotificationListResult {
  const body = response as {
    data?: NotificationListResult | AppNotification[] | { data?: unknown };
  };
  const nested = body?.data;

  if (nested && typeof nested === 'object' && Array.isArray((nested as NotificationListResult).items)) {
    const result = nested as NotificationListResult;
    return {
      items: result.items,
      total_count: result.total_count ?? result.items.length,
      total_all: result.total_all ?? result.total_count ?? result.items.length,
      unread_count: result.unread_count ?? countUnread(result.items),
      has_more: result.has_more ?? false,
    };
  }

  const legacyItems = unwrapList<AppNotification>(response);
  return {
    items: legacyItems,
    total_count: legacyItems.length,
    total_all: legacyItems.length,
    unread_count: countUnread(legacyItems),
    has_more: false,
  };
}

function buildListQueryParams(
  params: NotificationListParams = {
    scope: 'recent',
    hours: NOTIFICATION_RECENT_HOURS,
  }
): Record<string, string | number> {
  const scope = params.scope ?? 'recent';
  const query: Record<string, string | number> = {
    scope,
    limit: params.limit ?? NOTIFICATION_PAGE_SIZE,
    offset: params.offset ?? 0,
  };
  if (scope === 'recent') {
    query.hours = params.hours ?? NOTIFICATION_RECENT_HOURS;
  }
  return query;
}

export const notificationService = {
  list: async (
    params: NotificationListParams = {
      scope: 'recent',
      hours: NOTIFICATION_RECENT_HOURS,
    }
  ): Promise<NotificationListResult> => {
    const response = await apiClient.get(
      '/get-notifications',
      buildListQueryParams(params)
    );
    return unwrapListResult(response);
  },

  unreadCount: async (
    hours: number = NOTIFICATION_RECENT_HOURS
  ): Promise<number> => {
    const response = await apiClient.get('/notifications/unread-count', { hours });
    return unwrapUnread(response);
  },

  markRead: async (notificationId: string): Promise<void> => {
    await apiClient.patch(`/notifications/${notificationId}/read`, {});
  },
};

export function isAppointmentNotification(type?: string): boolean {
  return Boolean(type && type.startsWith('appointment_'));
}

export function isDicomNotification(type?: string): boolean {
  return Boolean(
    type &&
      (type.startsWith('new_') ||
        type.startsWith('dicom_') ||
        type === 'new_study' ||
        type === 'new_instance' ||
        type === 'new_series')
  );
}

export function isPredictionNotification(type?: string): boolean {
  return Boolean(type && type.startsWith('prediction_'));
}

export function isClinicalCollaborationNotification(
  item: Pick<AppNotification, 'type' | 'category'>
): boolean {
  if (item.category === 'collaboration' || item.category === 'clinical_collaboration') return true;
  const normalized = (item.type ?? '').toLowerCase();
  return ['opinion_requested', 'opinion_replied', 'discussion_reply'].includes(
    normalized
  );
}

function notificationLabelKey(
  prefix: 'types' | 'categories',
  value?: string | null
): string {
  const id = value ?? 'default';
  const key = `${prefix}.${id}`;
  return i18n.exists(key, { ns: NOTIFICATIONS_NS }) ? key : `${prefix}.default`;
}

export function notificationTypeLabel(
  type?: string,
  t?: (key: string) => string
): string {
  if (t) {
    return t(notificationLabelKey('types', type));
  }
  switch (type) {
    case 'appointment_created':
      return 'New appointment';
    case 'appointment_updated':
      return 'Rescheduled';
    case 'appointment_cancelled':
      return 'Cancelled';
    case 'appointment_reminder':
      return 'Reminder';
    case 'new_instance':
      return 'New exam';
    case 'new_study':
      return 'New study';
    case 'new_series':
      return 'New series';
    case 'prediction_completed':
      return 'Prediction done';
    case 'prediction_started':
      return 'Predicting';
    case 'prediction_failed':
      return 'Prediction failed';
    case 'opinion_requested':
      return 'Opinion requested';
    case 'opinion_replied':
      return 'Reply to opinion';
    case 'discussion_reply':
      return 'Discussion';
    default:
      return 'Notification';
  }
}

export function notificationCategoryLabel(
  category?: string | null,
  t?: (key: string) => string
): string {
  if (t) {
    return t(notificationLabelKey('categories', category));
  }
  switch (category) {
    case 'scheduling':
      return 'Scheduling';
    case 'dicom':
      return 'PACS';
    case 'prediction':
      return 'AI';
    case 'collaboration':
    case 'clinical_collaboration':
      return 'Collaboration';
    case 'system':
      return 'System';
    default:
      return 'Update';
  }
}

export type NotificationNavData = {
  study_instance_uid?: string;
  serie_instance_uid?: string;
  discussion_id?: string;
  root_id?: string;
  target_type?: string;
  target_ref?: string;
  target_label?: string;
  frame_id?: string;
  sop_instance_uid?: string;
  frame_number?: number;
  view_name?: string;
  view_code?: string;
  appointment_id?: string;
  start_time?: string;
  previous_start?: string;
  assignee_id?: string;
  patient_id?: string;
  patient_name?: string;
  sender_name?: string;
  appointment_type?: string;
  actor_name?: string;
  action?: string;
};

type NotificationTFunction = (
  key: string,
  options?: Record<string, unknown>
) => string;

const APPOINTMENT_TYPE_PATTERNS: { pattern: RegExp; key: string }[] = [
  { pattern: /^(t1)$|1st|1er|first/i, key: 'modal.scanTypes.t1' },
  { pattern: /^(t2)$|2nd|2e|second/i, key: 'modal.scanTypes.t2' },
  { pattern: /^(t3)$|3rd|3e|third/i, key: 'modal.scanTypes.t3' },
  { pattern: /^(routine)$|routine|contrôle/i, key: 'modal.scanTypes.routine' },
  { pattern: /^(emergency)$|emergency|urgence|urgent/i, key: 'modal.scanTypes.emergency' },
  { pattern: /^(gyn)$|gyn|pelvic/i, key: 'modal.scanTypes.gyn' },
  { pattern: /^(admin)$|admin|block/i, key: 'modal.scanTypes.admin' },
];

/** Map i18next language code to a dayjs locale id. */
export function dayjsLocaleFromLanguage(language?: string): string {
  if (!language) return 'en';
  if (language.startsWith('fr')) return 'fr';
  return 'en';
}

export function formatNotificationTimestamp(
  value: string,
  language?: string,
  format = 'MMM D, HH:mm'
): string {
  const locale = dayjsLocaleFromLanguage(language);
  return dayjs(value).locale(locale).format(format);
}

function formatNotificationDateTime(
  value: string | undefined,
  t: NotificationTFunction,
  language?: string
): string {
  if (!value) return t('content.emptyDate');
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  const locale = dayjsLocaleFromLanguage(language);
  return parsed.locale(locale).format(t('dateTimeFormat'));
}

function parseActorFromContent(content?: string): string | null {
  if (!content) return null;
  const bookedBy = content.match(/\((?:booked by|réservé par) (.+?)\)$/i);
  if (bookedBy?.[1]) return bookedBy[1].trim();
  const byActor = content.match(/\((?:by|par) (.+?)\)$/i);
  if (byActor?.[1]) return byActor[1].trim();
  return null;
}

function parsePreviousStartFromContent(content?: string): string | null {
  if (!content) return null;
  const match = content.match(/(?:moved from|déplacé de) (.+?) (?:to|à) /i);
  return match?.[1]?.trim() ?? null;
}

function translateAppointmentType(
  appointmentType: string | undefined,
  tScheduling?: NotificationTFunction,
  t?: NotificationTFunction
): string {
  const fallback = appointmentType?.trim() || t?.('content.defaultVisit') || 'Visit';
  if (!tScheduling || !appointmentType) return fallback;

  for (const { pattern, key } of APPOINTMENT_TYPE_PATTERNS) {
    if (pattern.test(appointmentType)) {
      return tScheduling(key);
    }
  }
  return fallback;
}

function buildAppointmentContent(
  item: AppNotification,
  t: NotificationTFunction,
  tScheduling: NotificationTFunction | undefined,
  language: string | undefined,
  showPatientSeparately: boolean
): string | null {
  const data = (item.data ?? {}) as NotificationNavData;
  if (!data.patient_name && !data.start_time && !data.appointment_type) {
    return null;
  }

  const patient =
    data.patient_name?.trim() ||
    getNotificationPatientName(item) ||
    t('content.defaultPatient');
  const visitType = translateAppointmentType(data.appointment_type, tScheduling, t);
  const when = formatNotificationDateTime(data.start_time, t, language);
  const actorName =
    data.actor_name?.trim() ||
    parseActorFromContent(item.content) ||
    t('content.defaultStaff');
  const actorPhrase =
    item.type === 'appointment_created'
      ? t('content.actorBookedBy', { name: actorName })
      : t('content.actorBy', { name: actorName });

  const previousWhen =
    data.previous_start ||
    parsePreviousStartFromContent(item.content) ||
    null;

  const detail =
    item.type === 'appointment_updated' && previousWhen && previousWhen !== when
      ? t('content.appointmentRescheduledDetail', {
          visitType,
          when,
          previousWhen: formatNotificationDateTime(previousWhen, t, language),
          actorPhrase,
        })
      : t('content.appointmentDetail', { visitType, when, actorPhrase });

  if (showPatientSeparately) return detail;
  return t('content.withPatient', { patient, detail });
}

function extractSenderFromCollaborationContent(content: string): string | null {
  const patterns = [
    /^(.+?) requested your review on an exam$/i,
    /^(.+?) replied to your review request$/i,
    /^(.+?) commented on (.+)$/i,
    /^(.+?) replied on (.+)$/i,
  ];
  for (const pattern of patterns) {
    const match = content.match(pattern);
    if (match?.[1]) return match[1].trim();
  }
  return null;
}

function extractLocationFromCollaborationContent(content: string): string | null {
  const commentMatch = content.match(/^(.+?) commented on (.+)$/i);
  if (commentMatch?.[2]) return commentMatch[2].trim();
  const replyMatch = content.match(/^(.+?) replied on (.+)$/i);
  if (replyMatch?.[2]) return replyMatch[2].trim();
  return null;
}

function buildCollaborationNotificationContent(
  item: AppNotification,
  t: NotificationTFunction,
  patient: string,
  showPatientSeparately = false
): string {
  const data = (item.data ?? {}) as NotificationNavData;
  const sender =
    data.sender_name?.trim() ||
    extractSenderFromCollaborationContent(item.content) ||
    t('content.defaultColleague');
  const location = formatCollaborationLocationLabel({
    target_type: data.target_type,
    target_label: data.target_label,
    view_name: data.view_name,
    view_code: data.view_code,
    frame_number: data.frame_number,
  });
  const showPatientInBody =
    !showPatientSeparately &&
    (Boolean(data.patient_name?.trim()) || Boolean(getNotificationPatientName(item)));

  switch (item.type) {
    case 'opinion_requested':
      return showPatientInBody
        ? t('content.opinionRequestedWithPatient', { sender, patient })
        : t('content.opinionRequested', { sender });
    case 'opinion_replied':
      return showPatientInBody
        ? t('content.opinionRepliedWithPatient', { sender, patient })
        : t('content.opinionReplied', { sender });
    case 'discussion_reply':
      if (/commented on/i.test(item.content)) {
        return t('content.discussionComment', { sender, location });
      }
      return t('content.discussionReply', { sender, location });
    default:
      return item.content;
  }
}

/** Localized notification body; falls back to stored content for legacy rows. */
export function formatNotificationContent(
  item: AppNotification,
  t: NotificationTFunction,
  options?: {
    tScheduling?: NotificationTFunction;
    language?: string;
    showPatientSeparately?: boolean;
  }
): string {
  const { tScheduling, language, showPatientSeparately = false } = options ?? {};

  if (isAppointmentNotification(item.type)) {
    const formatted = buildAppointmentContent(
      item,
      t,
      tScheduling,
      language,
      showPatientSeparately
    );
    if (formatted) return formatted;
    return item.content;
  }

  const data = (item.data ?? {}) as NotificationNavData;
  const patient =
    data.patient_name?.trim() ||
    getNotificationPatientName(item) ||
    t('content.defaultPatient');

  switch (item.type) {
    case 'new_instance':
      return t('content.newInstance', { patient });
    case 'new_study':
      return t('content.newStudy', { patient });
    case 'new_series':
      return t('content.newSeries', { patient });
    case 'prediction_completed':
      if (
        item.content?.toLowerCase().includes('blind sweep') ||
        item.content?.toLowerCase().includes('balayage aveugle')
      ) {
        return t('content.predictionBlindCompleted', { patient });
      }
      return t('content.predictionCompleted', { patient });
    case 'prediction_started':
      return t('content.predictionStarted', { patient });
    case 'prediction_failed':
      return t('content.predictionFailed', { patient });
    case 'dicom_upload_completed':
      return t('content.dicomUploadCompleted', { patient });
    case 'dicom_upload_failed':
      return t('content.dicomUploadFailed', { patient });
    case 'conversion_completed':
      return t('content.conversionCompleted', { patient });
    case 'conversion_failed':
      return t('content.conversionFailed', { patient });
    case 'processing_started':
      return t('content.processingStarted', { patient });
    case 'processing_completed':
      return t('content.processingCompleted', { patient });
    case 'processing_failed':
      return t('content.processingFailed', { patient });
    case 'report_generated':
      return t('content.reportGenerated', { patient });
    case 'report_shared':
      return t('content.reportShared', { patient });
    case 'report_failed':
      return t('content.reportFailed', { patient });
    case 'system_maintenance':
      return t('content.systemMaintenance');
    case 'system_update':
      return t('content.systemUpdate');
    case 'system_error':
      return t('content.systemError');
    case 'user_login':
      return t('content.userLogin');
    case 'user_logout':
      return t('content.userLogout');
    case 'user_action':
      return t('content.userAction');
    case 'opinion_requested':
    case 'opinion_replied':
    case 'discussion_reply':
      if (isClinicalCollaborationNotification(item)) {
        return buildCollaborationNotificationContent(
          item,
          t,
          patient,
          showPatientSeparately
        );
      }
      return item.content;
    default:
      if (data.patient_name || getNotificationPatientName(item)) {
        return t('content.genericWithPatient', { patient });
      }
      return item.content;
  }
}

/** Patient label from structured data or notification content. */
export function getNotificationPatientName(item: AppNotification): string | null {
  const data = item.data as NotificationNavData | undefined;
  const fromData = data?.patient_name?.trim();
  if (fromData) return fromData;

  const content = item.content?.trim();
  if (!content) return null;

  // Legacy appointment lines (EN + FR)
  const appointmentMatch = content.match(
    /^(?:New appointment|Nouveau rendez-vous|Appointment (?:cancelled|updated|rescheduled)|Rendez-vous (?:annulé|replanifié|modifié)): ([^—]+?) —/i
  );
  if (appointmentMatch?.[1]) {
    const name = appointmentMatch[1].trim();
    if (name && name.toLowerCase() !== 'patient') return name;
  }

  // Legacy DICOM / prediction: "… for Name" / "… pour Name"
  const forMatch = content.match(/\b(?:for|pour) (.+)$/i);
  if (forMatch?.[1]) {
    const name = forMatch[1].trim();
    if (name && !name.startsWith('study ') && name.toLowerCase() !== 'patient') {
      return name;
    }
  }

  return null;
}

/** Resolve in-app path for a notification click (matches Pages.tsx routes). */
export function getNotificationNavigationPath(item: AppNotification): string {
  const data = (item.data ?? {}) as NotificationNavData;

  if (isAppointmentNotification(item.type)) {
    const params = new URLSearchParams();
    const startTime = data.start_time;
    const date =
      startTime && dayjs(startTime).isValid()
        ? dayjs(startTime).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
    params.set('date', date);
    if (data.appointment_id) {
      params.set('appointment', data.appointment_id);
    }
    if (data.assignee_id) {
      params.set('assignee', data.assignee_id);
    }
    return `/Scheduling?${params.toString()}`;
  }

  if (isPredictionNotification(item.type) || isDicomNotification(item.type)) {
    if (data.study_instance_uid) {
      const viewerParams = new URLSearchParams({
        StudyInstanceUIDs: data.study_instance_uid,
      });
      return `/exam/viewer?${viewerParams.toString()}`;
    }
    return '/exams';
  }

  if (isClinicalCollaborationNotification(item)) {
    const viewerParams = new URLSearchParams();
    if (data.study_instance_uid) {
      viewerParams.set('StudyInstanceUIDs', String(data.study_instance_uid));
    }
    viewerParams.set('panel', 'collaboration');

    const threadId = data.root_id ?? data.discussion_id;
    if (threadId) {
      viewerParams.set('discussionId', String(threadId));
    }
    if (data.target_type) {
      viewerParams.set('target_type', String(data.target_type));
    }
    if (data.target_ref) {
      viewerParams.set('target_ref', String(data.target_ref));
    }
    if (data.target_label) {
      viewerParams.set('target_label', String(data.target_label));
    }

    const targetType = String(data.target_type ?? '').toUpperCase();
    const frameId =
      data.frame_id ?? (targetType === 'FRAME' ? data.target_ref : undefined);
    if (frameId) {
      viewerParams.set('frameId', String(frameId));
      viewerParams.set('tab', 'generalViewer');
      if (data.sop_instance_uid) {
        viewerParams.set('sopInstanceUID', String(data.sop_instance_uid));
      }
    } else if (targetType === 'REPORT') {
      viewerParams.set('tab', 'report');
      viewerParams.set('reportSection', 'formulaire');
    }

    storeExamViewerReturn('/exams');
    return `/exam/viewer?${viewerParams.toString()}`;
  }

  return '/exams';
}
